import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Card,
    CardContent,
    Grid,
    Avatar,
    useTheme,
    alpha,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    List,
    ListItemButton,
    ListItemText,
    CircularProgress,
    Box
} from '@mui/material';
import {
    Business as EntityIcon,
    Person as IndividualIcon,
    Close as CloseIcon,
    People as PeopleIcon,
    PersonAdd as PersonAddIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { BORROWER_TYPES, ENTITY_TYPES } from '../../utils/constants';
import { fetchBorrowers } from '../../services/userService';

const ENTITY_MAP = {
    partnership: { entityType: 'partnership_firm', businessType: 'partnership' },
    partnership_firm: { entityType: 'partnership_firm', businessType: 'partnership' },
    proprietorship: { entityType: 'propreitorship_firm', businessType: 'proprietorship' },
    propreitorship_firm: { entityType: 'propreitorship_firm', businessType: 'proprietorship' },
};
const getEntityAndBusiness = (raw) => {
    const key = (raw || '').toLowerCase();
    if (key.includes('partnership')) return ENTITY_MAP.partnership_firm;
    if (key.includes('proprietorship') || key.includes('propreitorship')) return ENTITY_MAP.proprietorship;
    return { entityType: 'company', businessType: 'company' };
};

const buildUserToPassFromBorrower = (borrower) => {
    const isEntity = borrower.borrowerType === 'entity' || borrower.userType === 'entity';
    const borrowerType = isEntity ? 'entity' : 'individual';
    const { entityType, businessType } = isEntity
        ? getEntityAndBusiness(borrower.entityType || borrower.borrowerType)
        : { entityType: '', businessType: '' };
    const userToPass = {
        ...borrower,
        id: borrower.id,
        uuid: borrower.uuid,
        name: borrower.name,
        email: borrower.email,
        phoneNumber: borrower.phoneNumber ?? borrower.mobileNumber,
        address: borrower.address,
        gstNumber: borrower.gstNumber,
        entityName: borrower.entityName,
        userType: borrowerType,
        borrowerType: entityType || borrowerType,
        entityType,
    };
    return { userToPass, borrowerType, businessType };
};

const ApplyLoanNowModal = ({ open, onClose }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [selectedBorrowerType, setSelectedBorrowerType] = useState('');
    const [selectedEntityType, setSelectedEntityType] = useState('');
    const [view, setView] = useState('choice');
    const [borrowers, setBorrowers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedBorrower, setSelectedBorrower] = useState(null);

    const handleClose = useCallback(() => {
        setSelectedBorrowerType('');
        setSelectedEntityType('');
        setView('choice');
        setBorrowers([]);
        setSelectedBorrower(null);
        onClose();
    }, [onClose]);

    const clearConflictingData = useCallback(() => {
        ['user', 'jwtToken', 'loanApplicationUser', 'userInfo'].forEach(key =>
            localStorage.removeItem(key)
        );
    }, []);

    useEffect(() => {
        if (!open || view !== 'list') return;
        let cancelled = false;
        setLoading(true);
        fetchBorrowers()
            .then((res) => {
                const data = res?.result?.result ?? res?.result ?? res ?? [];
                if (!cancelled) setBorrowers(Array.isArray(data) ? data : []);
            })
            .catch(() => { if (!cancelled) setBorrowers([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, view]);

    const handleChooseExisting = useCallback(() => setView('list'), []);
    const handleCreateNew = useCallback(() => {
        navigate('/users/borrowers', { state: { openAddBorrower: true } });
        handleClose();
    }, [navigate, handleClose]);

    const handleSelectBorrowerAndApply = useCallback(() => {
        if (!selectedBorrower) return;
        clearConflictingData();
        const { userToPass, borrowerType, businessType } = buildUserToPassFromBorrower(selectedBorrower);
        localStorage.setItem('userEntityType', borrowerType);
        localStorage.setItem('userBusinessType', businessType);
        localStorage.setItem('loanApplicationUser', JSON.stringify(userToPass));
        navigate('/apply-loan?source=borrower&new=true');
        handleClose();
    }, [selectedBorrower, clearConflictingData, navigate, handleClose]);

    const backToChoice = useCallback(() => {
        setView('choice');
        setSelectedBorrower(null);
    }, []);

    const handleBorrowerTypeSelect = useCallback((type) => {
        setSelectedBorrowerType(type);
        if (type === BORROWER_TYPES.INDIVIDUAL) {
            setSelectedEntityType('');
        }
    }, []);

    const handleEntityTypeSelect = useCallback((entityType) => {
        setSelectedEntityType(entityType);
    }, []);

    const handleApplyLoan = useCallback(() => {
        if (!selectedBorrowerType) return;

        clearConflictingData();

        if (selectedBorrowerType === BORROWER_TYPES.INDIVIDUAL) {
            localStorage.setItem('userBusinessType', 'individual');
            localStorage.setItem('userEntityType', '');
        } else if (selectedBorrowerType === BORROWER_TYPES.ENTITY && selectedEntityType) {
            localStorage.setItem('userBusinessType', 'entity');
            localStorage.setItem('userEntityType', selectedEntityType);
        } else {
            return;
        }

        setTimeout(() => {
            navigate('/apply-loan');
            handleClose();
        }, 100);
    }, [selectedBorrowerType, selectedEntityType, clearConflictingData, navigate, handleClose]);

    const entityTypeOptions = useMemo(() => [
        { value: ENTITY_TYPES.COMPANY, label: 'Company' },
        { value: ENTITY_TYPES.PARTNERSHIP_FIRM, label: 'Partnership Firm' },
        { value: ENTITY_TYPES.PROPRIETORSHIP_FIRM, label: 'Proprietorship Firm' }
    ], []);

    const choiceOptions = useMemo(() => [
        {
            id: 'existing',
            title: 'Choose from existing borrowers',
            description: 'Select a borrower from the list to start the loan application',
            icon: <PeopleIcon fontSize="large" />,
            iconColor: theme.palette.primary.main,
            iconBgColor: alpha(theme.palette.primary.main, 0.15),
            onClick: handleChooseExisting,
        },
        {
            id: 'create',
            title: 'Create new borrower',
            description: 'Add a new borrower and then apply for a loan',
            icon: <PersonAddIcon fontSize="large" />,
            iconColor: theme.palette.secondary.main,
            iconBgColor: alpha(theme.palette.secondary.main, 0.15),
            onClick: handleCreateNew,
        },
    ], [theme.palette, handleChooseExisting, handleCreateNew]);

    const borrowerTypes = useMemo(() => [
        {
            id: BORROWER_TYPES.INDIVIDUAL,
            title: 'Individual Borrower',
            description: 'Apply for a personal loan as an individual borrower',
            icon: <IndividualIcon fontSize="large" />,
            iconColor: theme.palette.primary.main,
            iconBgColor: alpha(theme.palette.primary.main, 0.15)
        },
        {
            id: BORROWER_TYPES.ENTITY,
            title: 'Entity Borrower',
            description: 'Apply for a business loan as a company, partnership, or proprietorship',
            icon: <EntityIcon fontSize="large" />,
            iconColor: theme.palette.secondary.main,
            iconBgColor: alpha(theme.palette.secondary.main, 0.15)
        }
    ], [theme.palette]);

    const dialogStyles = useMemo(() => ({
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 12px 30px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        backgroundImage: theme.palette.mode === 'light'
            ? 'linear-gradient(to bottom, #ffffff, #f8faff)'
            : 'linear-gradient(to bottom, #1a1f2e, #131723)',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '5px',
            background: `linear-gradient(90deg, 
                ${theme.palette.primary.dark} 0%, 
                ${theme.palette.primary.main} 50%,
                ${theme.palette.primary.dark} 100%)`
        }
    }), [theme.palette]);

    const dialogTitleStyles = useMemo(() => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: 3,
        background: alpha(theme.palette.primary.main, 0.04),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
    }), [theme.palette]);

    const buttonStyles = useMemo(() => ({
        borderRadius: '10px',
        textTransform: 'none',
        px: 3,
        py: 1.25,
        fontWeight: 600,
        fontSize: '0.95rem',
        borderColor: alpha(theme.palette.divider, 0.5),
        color: theme.palette.text.secondary,
        fontFamily: "'Poppins', sans-serif",
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.04)
        }
    }), [theme.palette]);


    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: dialogStyles }}
        >
            <DialogTitle sx={dialogTitleStyles}>
                <Typography
                    variant="h6"
                    component="div"
                    fontWeight="bold"
                    sx={{
                        color: theme.palette.text.primary,
                        fontFamily: "'Poppins', sans-serif",
                        letterSpacing: '0.02em',
                        fontSize: '1.125rem'
                    }}
                >
                    {view === 'choice' ? 'Apply Loan' : 'Choose from existing borrowers'}
                </Typography>
                <IconButton
                    edge="end"
                    onClick={handleClose}
                    aria-label="close"
                    sx={{
                        color: alpha(theme.palette.primary.main, 0.8),
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            transform: 'rotate(90deg)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                {view === 'choice' && (
                    <>
                        <Typography variant="body1" sx={{ mb: 3, color: alpha(theme.palette.text.primary, 0.7), fontFamily: "'Poppins', sans-serif" }}>
                            Choose an existing borrower or create a new one to apply for a loan:
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 3, alignItems: 'stretch', justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                            {choiceOptions.map((opt) => (
                                <Grid item xs="auto" key={opt.id} sx={{ display: 'flex', flex: 1, justifyContent: 'center' }}>
                                    <Card
                                        onClick={opt.onClick}
                                        sx={{
                                            borderRadius: 3,
                                            cursor: 'pointer',
                                            width: '100%',
                                            minHeight: '200px',
                                            maxWidth: '280px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.3s ease',
                                            border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                                            boxShadow: '0 3px 15px rgba(0,0,0,0.05)',
                                            '&:hover': {
                                                transform: 'translateY(-3px)',
                                                boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                                                borderColor: alpha(opt.iconColor, 0.3),
                                                backgroundColor: alpha(opt.iconColor, 0.02),
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1.5, flex: 1, justifyContent: 'center' }}>
                                            <Avatar sx={{ bgcolor: opt.iconBgColor, color: opt.iconColor, width: 60, height: 60, mb: 1.5 }}>{opt.icon}</Avatar>
                                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5, textAlign: 'center', fontSize: '1rem' }}>{opt.title}</Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', fontFamily: "'Poppins', sans-serif", lineHeight: 1.3, fontSize: '0.85rem' }}>{opt.description}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}

                {view === 'list' && (
                    <>
                        <Button startIcon={<ArrowBackIcon />} onClick={backToChoice} sx={{ mb: 2, textTransform: 'none' }}>Back</Button>
                        <Typography variant="body2" sx={{ mb: 2, color: alpha(theme.palette.text.primary, 0.7) }}>Select a borrower to apply for a loan:</Typography>
                        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
                        {!loading && !borrowers.length && <Typography color="textSecondary">No borrowers found.</Typography>}
                        {!loading && !!borrowers.length && (
                            <List sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                {borrowers.map((b) => (
                                    <ListItemButton
                                        key={b.uuid ?? b.id}
                                        selected={selectedBorrower?.uuid === b.uuid || selectedBorrower?.id === b.id}
                                        onClick={() => setSelectedBorrower(b)}
                                    >
                                        <ListItemText
                                            primary={b.name || b.email || 'Unnamed'}
                                            secondary={[b.email, b.borrowerType || b.entityType].filter(Boolean).join(' • ')}
                                        />
                                    </ListItemButton>
                                ))}
                            </List>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    px: 4,
                    py: 3,
                    background: alpha(theme.palette.primary.main, 0.03),
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    justifyContent: view === 'list' ? 'space-between' : 'flex-end',
                }}
            >
                <Button onClick={view === 'list' ? backToChoice : handleClose} variant="outlined" sx={buttonStyles}>Cancel</Button>
                {view === 'list' && (
                    <Button
                        onClick={handleSelectBorrowerAndApply}
                        variant="contained"
                        disabled={!selectedBorrower}
                        sx={{
                            ...buttonStyles,
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            '&:hover': { backgroundColor: theme.palette.primary.dark },
                            '&:disabled': { backgroundColor: alpha(theme.palette.primary.main, 0.3), color: alpha(theme.palette.text.secondary, 0.5) },
                        }}
                    >
                        Apply Loan
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ApplyLoanNowModal;
